import type { SectionState } from "./dto";

// F171 (Tomas Pohl 2026-10-06): "Napojení plateb", the step the TEAM does for a
// client who bought the payment setup (`clients.includes_payments_setup`).
// ONE module for the Worker (step state, routes) and the console (checklist),
// so the two can never disagree about which items exist or what closes the step.

/**
 * The partner's checklist, in the order the work is done. Each key has a
 * label in `staffConsole.paymentsSetup.items.<key>` (cs + en).
 *
 * `access` is first and is the one item that moves the BALL: until the team
 * has a way into the client's Stripe (an invite as administrator/developer,
 * or a call where the client connects it with /setupStripe), the step is the
 * client's turn; after it, the team's.
 */
export const PAYMENTS_CHECKLIST_KEYS = [
  "access",
  "stripe_connected",
  "tariffs",
  "subscription_url",
  "invites",
  "order_links",
  "test_purchase"
] as const;

export type PaymentsChecklistKey = (typeof PAYMENTS_CHECKLIST_KEYS)[number];
export type PaymentsChecklist = Partial<Record<PaymentsChecklistKey, boolean>>;

export function isPaymentsChecklistKey(value: unknown): value is PaymentsChecklistKey {
  return typeof value === "string" && (PAYMENTS_CHECKLIST_KEYS as readonly string[]).includes(value);
}

/** Read a stored checklist; unknown keys and non-boolean values are dropped. */
export function parsePaymentsChecklist(raw: unknown): PaymentsChecklist {
  let value = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw) as unknown;
    } catch {
      return {};
    }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const checklist: PaymentsChecklist = {};
  for (const key of PAYMENTS_CHECKLIST_KEYS) {
    if ((value as Record<string, unknown>)[key] === true) checklist[key] = true;
  }
  return checklist;
}

export function paymentsChecklistComplete(checklist: PaymentsChecklist): boolean {
  return PAYMENTS_CHECKLIST_KEYS.every((key) => checklist[key] === true);
}

/** What the sales derivation needs to know about the step. */
export interface PaymentsSetupFacts {
  /** The client bought it (`clients.includes_payments_setup = 1`). */
  included: boolean;
  /** The partner ticked `access`: the ball is with the team. */
  accessReady: boolean;
  /** The partner marked the step done. */
  completed: boolean;
}

/**
 * The step's state. It opens on the APPROVED sales page (the order links are
 * part of the work and need the page), and nothing locks back: a step the
 * partner closed stays done.
 *
 * - `next`    = the client's turn: give the team access to their Stripe.
 * - `working` = the team's turn: access is there, the setup is running.
 * - `done`    = the partner marked the checklist done.
 */
export function paymentsStepState(facts: PaymentsSetupFacts, pageApproved: boolean): SectionState {
  if (facts.completed) return "done";
  if (!pageApproved) return "locked";
  return facts.accessReady ? "working" : "next";
}
